/**
 * Servicio de gestión de usuarios del sistema
 * NUEVO MODELO: Usuarios se registran con email
 *
 * Los usuarios se crean al registrarse en la colección "users"
 * Los roles se asignan mediante roleAssignments
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { updatePassword } from "firebase/auth";
import { db, auth } from "@/lib/firebase";
import type { SystemUser } from "@/types";

const COLLECTION = "users";

export const userService = {
  /**
   * Obtiene todos los usuarios del sistema
   */
  async getAll(): Promise<SystemUser[]> {
    const usersRef = collection(db, COLLECTION);
    const q = query(usersRef, orderBy("email", "asc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as SystemUser[];
  },

  /**
   * Obtiene usuarios por estado
   */
  async getByStatus(
    status: "active" | "inactive" | "pending",
  ): Promise<SystemUser[]> {
    const usersRef = collection(db, COLLECTION);
    const q = query(
      usersRef,
      where("status", "==", status),
      orderBy("email", "asc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as SystemUser[];
  },

  /**
   * Obtiene un usuario por ID
   */
  async getById(id: string): Promise<SystemUser | null> {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as SystemUser;
  },

  /**
   * Obtiene un usuario por email
   */
  async getByEmail(email: string): Promise<SystemUser | null> {
    const usersRef = collection(db, COLLECTION);
    const q = query(usersRef, where("email", "==", email.toLowerCase()));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as SystemUser;
  },

  /**
   * Crea un nuevo usuario (solo usado internamente por auth)
   */
  async create(
    data: Omit<SystemUser, "id" | "createdAt" | "updatedAt">,
  ): Promise<SystemUser> {
    const userRef = doc(collection(db, COLLECTION));
    const now = serverTimestamp();

    await setDoc(userRef, {
      ...data,
      email: data.email.toLowerCase(),
      status: data.status || "pending",
      createdAt: now,
      updatedAt: now,
    });

    return {
      id: userRef.id,
      ...data,
    } as SystemUser;
  },

  /**
   * Actualiza un usuario existente
   */
  async update(
    id: string,
    data: Partial<
      Pick<SystemUser, "displayName" | "phone" | "status" | "photoUrl">
    >,
    updatedBy: string,
  ): Promise<void> {
    const userRef = doc(db, COLLECTION, id);
    await updateDoc(userRef, {
      ...data,
      updatedBy,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Desactiva un usuario
   */
  async deactivate(id: string, updatedBy: string): Promise<void> {
    const userRef = doc(db, COLLECTION, id);
    await updateDoc(userRef, {
      status: "inactive",
      updatedBy,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Activa un usuario
   */
  async activate(id: string, updatedBy: string): Promise<void> {
    const userRef = doc(db, COLLECTION, id);
    await updateDoc(userRef, {
      status: "active",
      updatedBy,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Cambiar contraseña del usuario actual
   */
  async changePassword(newPassword: string): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No hay usuario autenticado");
    }
    await updatePassword(currentUser, newPassword);
  },
};
